import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton, Tooltip } from "@mui/material";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { colors } from "../../../ui/colors";
import { AccountAvatarView } from "./AccountAvatarView";
import { getAccountAvatarSrc } from "../helpers/getAccountAvatarSrc";
import { getAccountInitial } from "../helpers/getAccountInitial";
import { LibraryIconsAccountAvatar } from "./LibraryIconsAccountAvatar";
import { ArchiveIconAvatar } from "../../CompanyAvatar/components/ArchiveIconsAvatar";

type EditableAccountAvatarProps = {
  account?: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    imageUrl?: string | null;
    avatar?: string | null;
  } | null;
  size?: number;
  disabled?: boolean;
  isLoading?: boolean;
  accept?: string;
  onSelectAvatarFile?: (file: File) => void;
};

export const EditableAccountAvatar = ({
  account,
  size = 100,
  disabled = false,
  isLoading = false,
  accept = "image/*",
  onSelectAvatarFile,
}: EditableAccountAvatarProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | undefined>();
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(menuAnchorEl);
  const src = previewSrc || getAccountAvatarSrc(account);
  const initial = getAccountInitial(account);
  const alt = [account?.firstName, account?.lastName].filter(Boolean).join(" ") || account?.email || "Account";
  const accountName = [account?.firstName, account?.lastName].filter(Boolean).join(" ") || account?.email || "Account";

  useEffect(() => {
    return () => {
      if (previewSrc) URL.revokeObjectURL(previewSrc);
    };
  }, [previewSrc]);

  const setPendingAvatarFile = (file: File) => {
    const nextPreviewSrc = URL.createObjectURL(file);
    setPreviewSrc((currentPreviewSrc) => {
      if (currentPreviewSrc) URL.revokeObjectURL(currentPreviewSrc);
      return nextPreviewSrc;
    });
    setPendingFile(file);
  };

  const handleOpenFilePicker = () => {
    if (disabled || isLoading) return;

    inputRef.current?.click();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleCancelAvatarChange = () => {
    setPendingFile(null);
    setPreviewSrc((currentPreviewSrc) => {
      if (currentPreviewSrc) URL.revokeObjectURL(currentPreviewSrc);
      return undefined;
    });
    handleCloseMenu();
  };

  const handleSaveAvatarChange = () => {
    if (!pendingFile) return;

    onSelectAvatarFile?.(pendingFile);
    setPendingFile(null);
    handleCloseMenu();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setPendingAvatarFile(file);
    }

    event.target.value = "";
  };

  const handleSelectLibraryAvatar = async (avatar: ArchiveIconAvatar) => {
    try {
      const response = await fetch(avatar.src);
      const blob = await response.blob();
      const extension = blob.type === "image/webp" ? "webp" : blob.type === "image/svg+xml" ? "svg" : "png";
      const file = new File([blob], `${avatar.name}.${extension}`, { type: blob.type || "image/png" });

      setPendingAvatarFile(file);
    } catch (error) {
      console.error("Error selecting account avatar:", error);
    }
  };

  return (
    <>
      <Box
        data-account-avatar-editor="true"
        sx={{
          position: "relative",
          width: size,
          height: size,
          "&:hover .account-avatar-edit-button": {
            opacity: 1,
            transform: "scale(1)",
          },
        }}
      >
        <AccountAvatarView src={src} initial={initial} size={size} alt={alt} />

        <Tooltip title="Edit account profile" arrow>
          <Box component="span" sx={{ display: "inline-flex" }}>
            <IconButton
              size="small"
              className="account-avatar-edit-button"
              aria-label="Edit account profile"
              onClick={handleOpenMenu}
              disabled={disabled || isLoading}
              sx={{
                position: "absolute",
                right: -4,
                bottom: -4,
                width: 28,
                height: 28,
                bgcolor: colors.orange.base,
                border: `1px solid ${colors.orange[100]}`,
                boxShadow: "0 2px 6px rgba(0,0,0,0.14)",
                color: "#fff",
                opacity: 1,
                transform: "scale(1)",
                transition: "opacity 0.15s ease, transform 0.15s ease, background-color 0.15s ease",
                "&:hover": {
                  bgcolor: colors.orange[600],
                },
                "&.Mui-disabled": {
                  bgcolor: colors.gray[200],
                  color: colors.gray[400],
                },
              }}
            >
              <EditIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Box>
        </Tooltip>

        <LibraryIconsAccountAvatar
          open={isMenuOpen}
          anchorEl={menuAnchorEl}
          disabled={disabled}
          onChangeAvatar={handleOpenFilePicker}
          onDropAvatar={setPendingAvatarFile}
          onSelectAvatar={handleSelectLibraryAvatar}
          hasPendingAvatar={Boolean(pendingFile)}
          isSaving={isLoading}
          onSave={handleSaveAvatarChange}
          onCancel={handleCancelAvatarChange}
          avatarSrc={src}
          avatarInitial={initial}
          avatarAlt={alt}
          accountName={accountName}
          accountRole="Account"
        />

        <input ref={inputRef} accept={accept} type="file" hidden onChange={handleFileChange} />
      </Box>
    </>
  );
};
